import { useCallback, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { getSeasonalChartData } from '@/components/Chart/api/getChartData';
import type {
  ChartDataRange,
  KeyData,
  SeriesData,
  SeriesMetadata
} from '@/components/Chart/types/chartData';
import { ExtendedSeriesOptionsType } from '@/components/Chart/types/highcharts';
import { findSeriesBasedOnKey } from '@/components/Chart/utils/findSeriesBasedOnKey';
import { getFieldDecimalPlaces } from '@/components/Chart/utils/getFieldDecimalPlaces';
import { getSeriesId } from '@/components/Chart/utils/getSeriesId';
import { getSeriesName } from '@/components/Chart/utils/getSeriesName';
import { getShortTitle } from '@/components/Chart/utils/getShortTitle';
import { parseSeasonalSeriesDataToSeries } from '@/components/Chart/utils/parseSeasonalSeriesDataToSeries';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import getDecimalPlaces from '@/utils/getDecimalPlaces';
import { buildChartLineSeries, buildChartRangeSeries } from '../utils/buildChartLineSeries';

function parseDataSeries(
  symbolIndex: number,
  series: SeriesData,
  fieldIndex: number,
  metadata: SeriesMetadata,
  field: string,
  shortTitle: string | undefined,
  key: KeyData
) {
  const combinedIndex = symbolIndex * Object.keys(series).length + fieldIndex;
  const decimalPlaces = metadata.Increment
    ? getDecimalPlaces(metadata.Increment)
    : getFieldDecimalPlaces(series?.[field]);
  const name = getSeriesName(shortTitle, metadata.Title);
  const id = getSeriesId(key, field);
  const dataRange = Object.keys(series)
    .map(Number)
    .filter(field => field && !Number.isNaN(field));

  const min = Math.min(...dataRange);
  const max = Math.max(...dataRange) - 1;
  const context = {
    ...key,
    ...metadata,
    decimalPlaces,
    combinedIndex,
    field
  };
  const timeRef = key.TimeRef;
  const timezone = metadata.TimeZone;

  const timeSeriesPoints = parseSeasonalSeriesDataToSeries(
    series,
    field,
    decimalPlaces,
    timeRef,
    timezone
  );

  if (field === 'range') {
    if (min >= max) return null;
    return buildChartRangeSeries({
      id: `${id} ${min}-${max}`,
      name: `${name} ${min}-${max}`,
      timeSeriesPoints,
      context
    });
  } else if (field === 'average') {
    if (min >= max) return null;
    return buildChartLineSeries({
      id: `${id} ${min}-${max} Avg`,
      name: `${name} ${min}-${max} Avg`,
      timeSeriesPoints,
      context
    });
  }
  return buildChartLineSeries({
    id: id + (field ? ` ${field}` : ''),
    name: name + (field ? ` ${field}` : ''),
    timeSeriesPoints,
    context,
    visible: fieldIndex > Object.keys(series).length - 5
  });
}

const useSeasonalChartSeries = (
  templateChartSeries: TemplateChartSeries[] | undefined,
  chartDataRange: ChartDataRange
) => {
  const {
    data: seasonalChartData,
    isError: isSeasonalChartDataError,
    isLoading: isSeasonalChartDataLoading
  } = useQuery({
    queryKey: [templateChartSeries?.[0], chartDataRange],
    queryFn: () => {
      if (!templateChartSeries?.[0]) return Promise.resolve(null);
      return getSeasonalChartData(templateChartSeries?.[0], chartDataRange);
    },
    enabled: !!templateChartSeries,
    refetchOnWindowFocus: false,
    gcTime: Infinity
  });

  const parseSeasonalDataToChartSeries = useCallback(
    (series: SeriesData, symbolIndex: number, key: KeyData, metadata: SeriesMetadata) => {
      const templateSeries = findSeriesBasedOnKey(templateChartSeries, key);
      const shortTitle = getShortTitle(templateSeries);
      return Object.keys(series)
        .map((field: string, fieldIndex) => {
          return parseDataSeries(symbolIndex, series, fieldIndex, metadata, field, shortTitle, key);
        })
        .filter((s): s is ExtendedSeriesOptionsType => s !== null);
    },
    [templateChartSeries]
  );
  const chartSeries = useMemo(() => {
    if (seasonalChartData?.symbols)
      return seasonalChartData.symbols.flatMap(({ key, series, metadata }, symbolIndex) =>
        parseSeasonalDataToChartSeries(series, symbolIndex, key, metadata)
      );
  }, [seasonalChartData, parseSeasonalDataToChartSeries]);

  return {
    loading: isSeasonalChartDataLoading || !templateChartSeries,
    error: isSeasonalChartDataError,
    chartSeries
  };
};

export default useSeasonalChartSeries;
